<script setup>
import { RouterLink } from 'vue-router'
import { ROUTES } from '../../../../core/config/constants.js'
import BusinessBanner from './BusinessBanner.vue'

defineEmits(['scroll-to-stats'])
</script>

<template>
  <!-- Tablet Frame (visible only on tablet 768px-1024px) -->
  <div class="tablet-wrapper">
    <!-- Hero content for tablet -->
    <div class="hero-content tablet-hero-content">
      <div class="hero-badge-wrapper">
        <div class="hero-badge">
          <span class="badge-dot"></span>
          <span>מובילים בייבוא מזון בישראל</span>
        </div>
        <div class="hero-sub-badge">
          <span class="sub-badge-dot"></span>
          <span>ועכשיו גם פארם</span>
        </div>
      </div>
      <h1 class="hero-title">
        <span class="title-line">הבחירה של</span>
        <span class="title-highlight">הקמעונאים</span>
      </h1>
      <p class="hero-subtitle">
        חברת יבוא ושיווק מזון מובילה, עם אלפי לקוחות בכל רחבי הארץ. מותגים מובילים, מחירים תחרותיים,
        ומשלוחים לכל נקודה בישראל.
      </p>
      <div class="hero-actions">
        <RouterLink :to="{ name: ROUTES.CATALOG }" class="btn-primary-lg">
          <span>הקטלוג שלנו</span>
        </RouterLink>
        <button @click="$emit('scroll-to-stats')" class="btn-secondary-lg">
          <span>עוד עלינו</span>
        </button>
      </div>
    </div>

    <!-- Tablet SVG Frame -->
    <div class="tablet-visual">
      <div class="tablet-container">
        <svg
          class="tablet-svg"
          viewBox="0 0 400 540"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="tabletFrame" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color: #1e293b" />
              <stop offset="50%" style="stop-color: #334155" />
              <stop offset="100%" style="stop-color: #1e293b" />
            </linearGradient>
            <linearGradient id="tabletScreenGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style="stop-color: #f8fafc" />
              <stop offset="100%" style="stop-color: #e2e8f0" />
            </linearGradient>
            <linearGradient id="tabletGoldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color: #f59e0b" />
              <stop offset="100%" style="stop-color: #d97706" />
            </linearGradient>
            <linearGradient id="tabletGoldGradientLight" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color: #fbbf24" />
              <stop offset="100%" style="stop-color: #f59e0b" />
            </linearGradient>
            <filter id="tabletShadow" x="-20%" y="-10%" width="140%" height="130%">
              <feDropShadow
                dx="0"
                dy="20"
                stdDeviation="30"
                flood-color="#1e293b"
                flood-opacity="0.4"
              />
            </filter>
            <clipPath id="tabletScreenClip">
              <rect x="10" y="10" width="380" height="520" rx="20" />
            </clipPath>
          </defs>

          <!-- Tablet Body with Shadow -->
          <g filter="url(#tabletShadow)">
            <rect x="0" y="0" width="400" height="540" rx="28" fill="url(#tabletFrame)" />
            <rect
              x="2"
              y="2"
              width="396"
              height="536"
              rx="26"
              fill="none"
              stroke="rgba(255,255,255,0.1)"
              stroke-width="1"
            />
          </g>

          <!-- Screen -->
          <rect x="10" y="10" width="380" height="520" rx="20" fill="url(#tabletScreenGradient)" />

          <!-- Screen Content (clipped) -->
          <g clip-path="url(#tabletScreenClip)">
            <!-- Status Bar -->
            <rect x="10" y="10" width="380" height="40" fill="#1e293b" />
            <text x="200" y="36" text-anchor="middle" fill="white" font-size="14" font-weight="600">
              GoFood
            </text>

            <!-- Front Camera -->
            <circle cx="200" cy="20" r="4" fill="#0f172a" />

            <!-- App Header -->
            <rect x="10" y="50" width="380" height="50" fill="#1e293b" />

            <!-- Search Bar -->
            <rect
              x="24"
              y="112"
              width="352"
              height="40"
              rx="12"
              fill="white"
              stroke="#e2e8f0"
              stroke-width="1"
            />
            <circle cx="348" cy="132" r="10" fill="none" stroke="#94a3b8" stroke-width="2" />
            <line
              x1="341"
              y1="139"
              x2="336"
              y2="144"
              stroke="#94a3b8"
              stroke-width="2"
              stroke-linecap="round"
            />

            <!-- Card 1 - SALE badge (top-left) -->
            <rect
              x="24"
              y="168"
              width="170"
              height="140"
              rx="12"
              fill="white"
              stroke="#e2e8f0"
              stroke-width="1"
            />
            <rect x="34" y="178" width="150" height="65" rx="8" fill="#fef3c7" />
            <circle cx="109" cy="210" r="22" fill="url(#tabletGoldGradientLight)" opacity="0.6" />
            <text
              x="109"
              y="270"
              text-anchor="middle"
              fill="#334155"
              font-size="12"
              font-weight="600"
            >
              שוקולד
            </text>
            <rect x="49" y="280" width="120" height="22" rx="11" fill="#EF4444" />
            <text
              x="109"
              y="296"
              text-anchor="middle"
              fill="white"
              font-size="11"
              font-weight="700"
            >
              SALE
            </text>

            <!-- Card 2 - 50% badge (top-right) -->
            <rect
              x="206"
              y="168"
              width="170"
              height="140"
              rx="12"
              fill="white"
              stroke="#e2e8f0"
              stroke-width="1"
            />
            <rect x="216" y="178" width="150" height="65" rx="8" fill="#dbeafe" />
            <circle cx="291" cy="210" r="22" fill="#60a5fa" opacity="0.5" />
            <text
              x="291"
              y="270"
              text-anchor="middle"
              fill="#334155"
              font-size="12"
              font-weight="600"
            >
              חטיפים
            </text>
            <rect x="231" y="280" width="120" height="22" rx="11" fill="url(#tabletGoldGradient)" />
            <text
              x="291"
              y="296"
              text-anchor="middle"
              fill="white"
              font-size="11"
              font-weight="700"
            >
              50%
            </text>

            <!-- Card 3 - Discount badge (bottom-left) -->
            <rect
              x="24"
              y="320"
              width="170"
              height="140"
              rx="12"
              fill="white"
              stroke="#e2e8f0"
              stroke-width="1"
            />
            <rect x="34" y="330" width="150" height="65" rx="8" fill="#dcfce7" />
            <circle cx="109" cy="362" r="22" fill="#4ade80" opacity="0.5" />
            <text
              x="109"
              y="422"
              text-anchor="middle"
              fill="#334155"
              font-size="12"
              font-weight="600"
            >
              משקאות
            </text>
            <rect x="49" y="432" width="120" height="22" rx="11" fill="#8B5CF6" />
            <text
              x="109"
              y="448"
              text-anchor="middle"
              fill="white"
              font-size="10"
              font-weight="700"
            >
              DISCOUNT
            </text>

            <!-- Card 4 - חיסול badge (bottom-right) -->
            <rect
              x="206"
              y="320"
              width="170"
              height="140"
              rx="12"
              fill="white"
              stroke="#e2e8f0"
              stroke-width="1"
            />
            <rect x="216" y="330" width="150" height="65" rx="8" fill="#fce7f3" />
            <circle cx="291" cy="362" r="22" fill="#f472b6" opacity="0.5" />
            <text
              x="291"
              y="422"
              text-anchor="middle"
              fill="#334155"
              font-size="12"
              font-weight="600"
            >
              ממתקים
            </text>
            <rect x="231" y="432" width="120" height="22" rx="11" fill="#1e293b" />
            <text
              x="291"
              y="448"
              text-anchor="middle"
              fill="#FBBF24"
              font-size="11"
              font-weight="700"
            >
              חיסול
            </text>

            <!-- Bottom Navigation -->
            <rect x="10" y="475" width="380" height="55" fill="white" />
            <line x1="10" y1="475" x2="390" y2="475" stroke="#e2e8f0" stroke-width="1" />

            <!-- Nav Icons -->
            <g fill="none" stroke="#94a3b8" stroke-width="2">
              <path d="M80 492 L80 506 L94 506 L94 492 L87 485 L80 492" />
              <circle cx="160" cy="496" r="8" />
              <line x1="166" y1="502" x2="170" y2="506" />
            </g>
            <g fill="none" stroke="url(#tabletGoldGradient)" stroke-width="2.5">
              <circle cx="232" cy="504" r="3" fill="#F59E0B" />
              <circle cx="246" cy="504" r="3" fill="#F59E0B" />
              <path d="M222 486 L226 486 L230 500 L250 500 L254 490 L232 490" />
            </g>
            <g fill="none" stroke="#94a3b8" stroke-width="2">
              <circle cx="315" cy="492" r="6" />
              <path d="M305 508 C305 502 309 498 315 498 C321 498 325 502 325 508" />
            </g>

            <!-- Cart Badge -->
            <circle cx="252" cy="484" r="10" fill="url(#tabletGoldGradient)" />
            <text
              x="252"
              y="488"
              text-anchor="middle"
              fill="white"
              font-size="11"
              font-weight="700"
            >
              3
            </text>
          </g>

          <!-- Home Indicator -->
          <rect x="150" y="524" width="100" height="5" rx="2.5" fill="#334155" />
        </svg>

        <!-- Floating Shopping Carts for Tablet -->
        <div class="tablet-floating-cart tablet-cart-1">
          <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="24" fill="white" />
            <circle cx="24" cy="24" r="22" fill="#FFFBEB" />
            <g transform="translate(12, 10)">
              <circle cx="8" cy="26" r="3" fill="#D97706" />
              <circle cx="20" cy="26" r="3" fill="#D97706" />
              <path
                d="M0 2 H4 L8 18 H22 L26 8 H10"
                stroke="#D97706"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="none"
              />
              <path d="M10 8 L22 8 L20 14 H10 Z" fill="#FEF3C7" />
            </g>
          </svg>
        </div>

        <div class="tablet-floating-cart tablet-cart-2">
          <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="20" r="20" fill="white" />
            <circle cx="20" cy="20" r="18" fill="#FEF3C7" />
            <g transform="translate(10, 8)">
              <rect x="2" y="6" width="16" height="12" rx="2" fill="#F59E0B" />
              <path
                d="M6 6 V4 C6 2 8 0 10 0 C12 0 14 2 14 4 V6"
                stroke="#D97706"
                stroke-width="2"
                fill="none"
              />
              <circle cx="10" cy="12" r="2" fill="white" />
            </g>
          </svg>
        </div>

        <div class="tablet-floating-cart tablet-cart-3">
          <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="18" cy="18" r="18" fill="white" />
            <circle cx="18" cy="18" r="16" fill="#FEF3C7" />
            <g transform="translate(8, 8)">
              <path d="M10 0 L12 6 L20 6 L18 0 Z" fill="#FBBF24" />
              <rect x="2" y="6" width="16" height="10" rx="2" fill="#F59E0B" />
              <rect x="6" y="16" width="2" height="4" fill="#D97706" />
              <rect x="12" y="16" width="2" height="4" fill="#D97706" />
            </g>
          </svg>
        </div>

        <div class="tablet-floating-cart tablet-cart-4">
          <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="16" fill="white" />
            <circle cx="16" cy="16" r="14" fill="#FFFBEB" />
            <g transform="translate(7, 7)">
              <path d="M9 0 L18 0 L16 6 L2 6 L0 0 Z" fill="#FBBF24" opacity="0.5" />
              <path d="M2 6 L4 14 L14 14 L16 6 Z" fill="#F59E0B" />
              <circle cx="5" cy="17" r="2" fill="#D97706" />
              <circle cx="13" cy="17" r="2" fill="#D97706" />
            </g>
          </svg>
        </div>

        <div class="tablet-floating-cart tablet-cart-5">
          <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="14" cy="14" r="14" fill="white" />
            <circle cx="14" cy="14" r="12" fill="#FEF3C7" />
            <text
              x="14"
              y="18"
              text-anchor="middle"
              fill="#D97706"
              font-size="14"
              font-weight="bold"
            >
              +
            </text>
          </svg>
        </div>

        <!-- Tablet sparkles -->
        <div class="tablet-sparkle tablet-sparkle-1"></div>
        <div class="tablet-sparkle tablet-sparkle-2"></div>
        <div class="tablet-sparkle tablet-sparkle-3"></div>
        <div class="tablet-sparkle tablet-sparkle-4"></div>
        <div class="tablet-sparkle tablet-sparkle-5"></div>
        <div class="tablet-sparkle tablet-sparkle-6"></div>
      </div>
    </div>

    <!-- Business Banner - Tablet -->
    <BusinessBanner variant="tablet" />
  </div>
</template>

<style src="../styles/hero-shared.css" scoped></style>
<style src="../styles/hero-tablet.css" scoped></style>
